export const COLORS = {
  COMPLETED: "#66F1AD",
  // Green for completed
  IN_PROGRESS: "#FC8EAC",
  // Pink for in-progress
  SELECTED: "#F6BE00",
  // Yellow for selected
  FAILED: "red",
  // Red for QC failures
  DEFAULT: "white",
  // White for default/not started
  LOCKED: "#529ef1" // Blue for locked items
};

// Helper function to determine contrast color based on background
export const getLuminance = color => {
  const rgb = Number.parseInt(color?.slice(1), 16);
  const r = rgb >> 16 & 0xff;
  const g = rgb >> 8 & 0xff;
  const b = rgb >> 0 & 0xff;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
export const getContrastColor = bgColor => {
  const luminance = getLuminance(bgColor);
  return luminance > 128 ? "#000000" : "#FFFFFF";
};
export const getColorCode = (value, data, type, userSelection, pierLegends) => {
  const prlegend = pierLegends[value];
  if (type === "CP" && userSelection?.showType === "bha") {
    let label;
    if (data?.pier_type_motor) {
      label = "sg_labels";
    } else {
      label = "bha_labels";
    }
    if (data?.bha_sg_labels) {
      if (data?.bha_sg_labels[0] === "E") {
        return prlegend?.[label]?.["E.Hvy"];
      } else if (data?.bha_sg_labels[0] === "L") {
        return prlegend?.[label]?.["Light"];
      } else if (data?.bha_sg_labels[0] === "H") {
        return prlegend?.[label]?.["Hvy"];
      } else {
        return `#4287f5`;
      }
    } else {
      return `#4287f5`;
    }
  } else {
    if (prlegend?.map_colour) {
      return prlegend?.map_colour;
    } else {
      return `#4287f5`;
    }
  }
};

// QC Status Checker Functions 

// Checks QC data and returns the appropriate color based on QC statuses

const getQCStatusColor = qcData => {
  if (!Array.isArray(qcData)) return COLORS.DEFAULT;

  // Check if any checkpoint has status=2 (fail)
  const anyStatus2 = qcData.some(item => item.qc_progress_status === 2);
  if (anyStatus2) {
    return COLORS.FAILED;
  }

  // Check if any checkpoint has null or 0 status
  const hasNullOrZeroStatus = qcData.some(item => item.qc_progress_status === null || item.qc_progress_status === 0);
  if (hasNullOrZeroStatus) {
    return COLORS.DEFAULT;
  }

  // If all have status 1 (pass)
  const allStatus1 = qcData.every(item => item.qc_progress_status === 1);
  if (allStatus1) {
    return COLORS.COMPLETED;
  }
  return COLORS.DEFAULT;
};

// Checks CP data and returns the appropriate color based on completion status

const getCPStatusColor = (data, selection) => {
  if (selection === "All") {
    if (typeof data === 'number') {
      if (data === 100) {
        return COLORS.COMPLETED;
      } else if (data > 0 && data < 100) {
        return COLORS.IN_PROGRESS;
      }
    }
  } else {
    if (data?.status === 2 || data?.completion_percentage === 100) {
      return COLORS.COMPLETED;
    } else if (data?.status === 1 || data?.completion_percentage > 0 && data?.completion_percentage < 100) {
      return COLORS.IN_PROGRESS;
    }
  }
  return COLORS.DEFAULT;
};

// Color Mapping Function 

// Generate a complete color mapping for all elements in the visualization
//This is a performance optimization to avoid calculating colors on-the-fly

export const generateColorMapping = (typeData, type, userSelection, selectedRows, activityType = null) => {
  if (!typeData || !type || !userSelection) return {};
  const selection = userSelection.selection || "All";
  const punchlistStatus = userSelection?.punchlistStatusSelection || 0;
  const colorMap = {};

  // First, identify selected tracker rows for quick lookup
  const selectedTrackerIds = new Set();
  if (selectedRows && selectedRows.sections) {
    Object.values(selectedRows.sections).forEach(section => {
      if (section.selectedRows) {
        Object.keys(section.selectedRows).forEach(trackerId => {
          selectedTrackerIds.add(trackerId);
        });
      }
    });
  }

  // Process data to assign colors
  if (type === "CP") {
    processCPData(typeData, selection, selectedTrackerIds, colorMap, activityType);
  } else if (type === "QC") {
    processQCData(typeData, selection, selectedTrackerIds, colorMap);
  } else if (type === "Punchlist") {
    processPunchlistData(typeData, selection, selectedTrackerIds, colorMap, punchlistStatus);
  }
  return colorMap;
};

// Process CP data and populate the colorMap

const processCPData = (typeData, selection, selectedTrackerIds, colorMap, activityType) => {
  // Process CP data - use the exact keys from typeData
  Object.keys(typeData).forEach(key => {
    // Convert selection to string to handle number activity IDs
    const selectionStr = String(selection).toLowerCase();
    // Skip keys that don't match the selection pattern
    if (selection !== "All" && !key.endsWith(`_${selectionStr}`) && !key.endsWith("_all")) {
      return;
    }

    // Extract tracker ID from the key (e.g., "123_activity_name")
    const parts = key.split('_');
    const trackerId = parts[0];

    // Check if this tracker is selected
    const isSelected = selectedTrackerIds.has(trackerId);

    // Get the data for this tracker/activity
    const data = typeData[key];

    // Initialize the colorMap entry for this key as an object with the main status
    if (!colorMap[key]) {
      colorMap[key] = {
        mainColor: isSelected ? COLORS.SELECTED : getCPStatusColor(data, selection),
        piers: {},
        torqueTubes: {},
        hasIndividualElements: false
      };
    }

    // For object data with piers and torque tubes, add them to the same tracker key
    if (activityType === "sub_activity" && data && typeof data === 'object' && data !== null) {
      // Process piers
      if (data.piers && Array.isArray(data.piers) && data.piers.length > 0) {
        data.piers.forEach(pier => {
          if (pier.status === 1) {
            colorMap[key].piers[pier.pier_id] = COLORS.COMPLETED;
            colorMap[key].hasIndividualElements = true;
          }
        });
      }

      // Process torque tubes
      if (data.torque_tubes && Array.isArray(data.torque_tubes) && data.torque_tubes.length > 0) {
        data.torque_tubes.forEach(tt => {
          if (tt.status === 1) {
            colorMap[key].torqueTubes[tt.torque_tube_id] = COLORS.COMPLETED;
            colorMap[key].hasIndividualElements = true;
          }
        });
      }
    }
  });
};

// Process QC data and populate the colorMap

const processQCData = (typeData, selection, selectedTrackerIds, colorMap) => {
  // Convert selection to string to handle number activity IDs
  const selectionStr = String(selection).toLowerCase();

  // Process QC data - preserve the exact keys from typeData
  Object.keys(typeData).forEach(key => {
    // Skip keys that don't match the selection pattern for QC
    if (selection !== "All" && !key.includes(`_${selectionStr}`)) {
      return;
    }
    const parts = key.split('_');
    const trackerId = parts[0];

    // Check if this tracker is selected
    const isSelected = selectedTrackerIds.has(trackerId);

    // Set color based on QC status
    if (isSelected) {
      colorMap[key] = COLORS.SELECTED;
    } else if (Array.isArray(typeData[key])) {
      colorMap[key] = getQCStatusColor(typeData[key]);
    } else {
      // Default if no array data
      colorMap[key] = COLORS.DEFAULT;
    }
  });
};

// Process Punchlist data and populate the colorMap
const processPunchlistData = (typeData, selection, selectedTrackerIds, colorMap, punchlistStatus) => {
  // Convert selection to string to handle number activity IDs
  const selectionStr = String(selection).toLowerCase();

  // Process Punchlist data - preserve the exact keys from typeData
  Object.keys(typeData).forEach(key => {
    // Skip keys that don't match the selection pattern for Punchlist
    if (selection !== "All" && !key.includes(`_${selectionStr}`)) {
      return;
    }
    const parts = key.split('_');
    const trackerId = parts[0];

    // Check if this tracker is selected
    const isSelected = selectedTrackerIds.has(trackerId);

    // Set color based on Punchlist status
    if (isSelected) {
      colorMap[key] = COLORS.SELECTED;
    } else if (Array.isArray(typeData[key])) {
      // Check if any item in the array has the selected punchlist status
      const hasSelectedStatus = typeData[key].some(item => item.qc_progress_punchlist_status === punchlistStatus);
      if (hasSelectedStatus) {
        // Show color based on the selected punchlist status
        if (punchlistStatus === 0) {
          colorMap[key] = COLORS.FAILED; // Red for Open
        } else if (punchlistStatus === 1) {
          colorMap[key] = COLORS.IN_PROGRESS; // Pink for Ready for Inspection
        } else if (punchlistStatus === 2) {
          colorMap[key] = COLORS.COMPLETED; // Green for Resolved
        } else {
          colorMap[key] = COLORS.DEFAULT;
        }
      } else {
        // No items with the selected status - show default
        colorMap[key] = COLORS.DEFAULT;
      }
    } else {
      // Default if no array data
      colorMap[key] = COLORS.DEFAULT;
    }
  });
};
export const generateLockMapping = (typeData, selectedRows) => {
  if (!typeData) return {};
  const lockMap = {};
  const selectedTrackerIds = new Set();
  if (selectedRows && selectedRows.sections) {
    Object.values(selectedRows.sections).forEach(section => {
      if (section.selectedRows) {
        Object.keys(section.selectedRows).forEach(trackerId => {
          selectedTrackerIds.add(trackerId);
        });
      }
    });
  }
  Object.keys(typeData).forEach(trackerId => {
    let trackerrowId = trackerId.split("_")[0];
    const isSelected = selectedTrackerIds.has(trackerrowId);
    if (typeData[trackerId]["lock"] && !isSelected) {
      lockMap[trackerrowId] = true;
    } else {
      lockMap[trackerrowId] = false;
    }
  });
  return lockMap;
};
export const getZimmermanLaydownColor = (laydownId, currentLaydownData) => {
  if (laydownId !== currentLaydownData && laydownId !== null) {
    return COLORS.COMPLETED;
  }
  return COLORS.DEFAULT;
};